/**
 * Kontext hinein, geprüfte Struktur heraus — für die drei KI-Assistenten
 * `assistSigePlan`, `assistKoordinierung` und `assistHandbuch`.
 *
 * ═══ WARUM DIESE DATEI ÜBERHAUPT EXISTIERT ═════════════════════════════════
 * Zwei Aufgaben, die beide NICHT in eine Komponente gehören:
 *
 *   1. ENTSCHEIDEN, WAS MITGEHT. Ein KI-Aufruf verlässt das Haus. Was hier
 *      nicht ausdrücklich abgeschrieben wird, kann auch nicht versehentlich
 *      mitgeschickt werden — die Kontexttypen sind deshalb schmal und werden
 *      Feld für Feld befüllt, niemals durch Weiterreichen eines Datensatzes.
 *      Was bewusst NICHT mitgeht, steht unten in einem eigenen Abschnitt.
 *
 *   2. DIE ANTWORT PRÜFEN. Was zurückkommt, ist Text eines Sprachmodells und
 *      keine Zusage über ein Format. Erst nach dem Gang durch die
 *      `lese…Antwort`-Funktionen ist es eine getippte Struktur; halbe oder
 *      abgeschnittene Antworten werden zu einem sauberen Fehlerergebnis und
 *      nie zu halben Daten in der Oberfläche.
 *
 * Rein: kein DOM, kein Supabase, kein React, kein `fetch`. Das Projekt
 * übersetzt seine Tests in der Node-Umgebung (kein jsdom) — eine Regel ist
 * nur dann geprüft, wenn sie HIER steht und nicht in einer Komponente.
 * Stehende Entscheidung im Repo: dünner Container, Logik in puren Funktionen.
 *
 * ═══ WAS BEWUSST NICHT IN DEN KONTEXT GEHT ═════════════════════════════════
 * Es sind fremde Firmen- und Personendaten, und die Assistenten brauchen
 * nichts davon, um ihre Frage zu beantworten:
 *
 *   • Anschriften jeder Art — Bauherr, Baumaßnahme, Firmensitz. Ein
 *     Vorschlag für eine Baustellenordnung wird nicht besser, wenn das Modell
 *     die Hausnummer kennt.
 *   • Telefonnummern, Mobilnummern, E-Mail-Adressen (auch die des SiGeKo).
 *   • Namen von Beschäftigten, Ersthelfern, Verantwortlichen, Ansprech-
 *     partnern; ebenso `contact_id`, `anzahl_beschaeftigte`, Unterschriften.
 *   • Alles aus dem Rechnungs- und Kundenzweig: Kundennummer, Kostenstelle,
 *     Auftragswert, Rechnungs- und Angebotsdaten, Zahlungsziele.
 *   • Der Notrufplan mit Krankenhaus, D-Arzt und Ersthelferliste — eine
 *     Sammlung von Anschriften und Telefonnummern.
 *   • Freitextfelder, die eine Anschrift oder einen Treffpunkt beschreiben
 *     können (`zugang`, `sammelplatz`), gehen NUR als Ja/Nein mit: ob sie
 *     erfasst sind, ist die Information, die der Assistent braucht; ihr
 *     Wortlaut ist es nicht.
 *
 * Die Regeln und Notfallschritte der Baustellenordnung gehen dagegen im
 * WORTLAUT mit — anders könnte der Assistent nicht sagen, welche Regel FEHLT.
 * Genau dort steht in der Praxis aber dasselbe, was der Notrufplan sperrt
 * („Ersthelfer Herr Schmidt (0171 2345678) verständigen"). Jede dieser Zeilen
 * läuft deshalb vor der Übergabe durch `ohnePersonendaten`; die Zusage oben
 * gilt damit auch für sie. Was der Filter nicht kann, steht bei ihm.
 *
 * MITGEHEN dürfen die Gewerksbezeichnungen aus dem Plan (`Elektro`,
 * `Rohbau`, gelegentlich mit Firmenzusatz im Wortlaut des Anwenders). Ohne
 * sie ließe sich keine Antwort einem Gewerk oder einem Paar zuordnen — der
 * Assistent wäre wertlos. Die Kontaktdaten dahinter bleiben trotzdem hier.
 *
 * Aus der PLANUNGSPHASE gehen Merkmale (nur als ja/nein/ungeklärt bzw. Zahl)
 * sowie Baubeschreibung und Bauaufgabe mit — beide Freitexte wie die Regeln
 * durch `ohnePersonendaten` und gekürzt (`planungKontext`). Fragen, Antworten,
 * Empfänger und Hinweise der Planungsphase gehen NICHT mit: dort stehen
 * Namen und Adressen der Planer.
 */

import { salvageJsonArray, isTruncatedAiResponse, rawAiText } from './aiJsonSalvage';
import type { ProjektMerkmale } from '../components/planungsphase/types';
// Nur die Merkmalsliste: `kapitel.ts` ist rein und importiert nichts von hier.
import { BOOLEAN_MERKMALE } from '../components/bauordnung/kapitel';

// ═══════════════════════════════════════════════════════════════════════════
// 0) OBERGRENZEN
// ═══════════════════════════════════════════════════════════════════════════
//
// Warum überhaupt gekappt wird: das Modell hat ein Token-Budget für die
// ANTWORT. Jedes Zeichen, das der Kontext verbraucht, fehlt dort — und eine
// am Limit abgeschnittene Antwort ist genau der Fall, den `aiJsonSalvage`
// mühsam wieder einsammelt. Gekappt wird deshalb HIER, an der Stelle, die die
// Daten kennt, und nicht als blinder `slice` über fertiges JSON (das erzeugt
// unparsebares JSON mitten im Prompt).
//
// Die Zahlen sind bewusst großzügig: ein SiGe-Plan mit mehr als 80
// Arbeitsschritten ist selten, und wo gekappt wird, sagt das der Kontext
// ausdrücklich (`weitereSchritte`, `weiterePaare`), damit das Modell nicht
// aus dem Schweigen auf Vollständigkeit schließt.

/** Arbeitsschritte je Plan-Anfrage. */
export const MAX_SCHRITTE = 80;
/** Gewerkepaare je Koordinierungs-Anfrage. */
export const MAX_PAARE = 40;
/** Katalogvorschläge, die je Paar als „schon gesagt" mitgehen. */
export const MAX_KATALOG_JE_PAAR = 6;
/** Bereits erfasste Gefährdungen je Arbeitsschritt. */
export const MAX_GEFAEHRDUNGEN_JE_SCHRITT = 6;
/** Listeneinträge in Handbuch-Kontext und -Antwort. */
export const MAX_LISTE = 20;
/** Einträge je Antwortliste. Mehr wäre keine Prüfliste mehr, sondern ein Berg. */
export const MAX_ANTWORT_EINTRAEGE = 15;
/** Zeichen je Freitextfeld. */
export const MAX_TEXTLAENGE = 400;

// ═══════════════════════════════════════════════════════════════════════════
// 1) KLEINE REINE HELFER
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Ein Freitextfeld, auf Länge gebracht.
 *
 * Die Kappung ist kein Schönheitsdienst: ein einzelnes Bemerkungsfeld kann
 * einen ganzen Brief enthalten. Uncapped verdrängt es die Daten, die die
 * Frage beantworten — und ein sehr langer Freitext ist zugleich der
 * wahrscheinlichste Träger versehentlich hineingeschriebener Personendaten.
 */
const text = (wert: unknown, max: number = MAX_TEXTLAENGE): string => {
    if (typeof wert !== 'string') return '';
    const sauber = wert.trim().replace(/\s+/g, ' ');
    return sauber.length > max ? `${sauber.slice(0, max)}…` : sauber;
};

const ISO_TAG = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Ein ISO-Tagesdatum oder `null`.
 *
 * Alles andere („KW 12", „März 2026", ein Zahlendreher) wird zu `null` und
 * damit sichtbar unbekannt. Ein weitergereichtes Halbdatum ließe das Modell
 * eine Gleichzeitigkeit behaupten, die niemand gerechnet hat — dieselbe
 * Strenge wie in `koordinierung/ueberschneidung.ts`.
 */
const datum = (wert: unknown): string | null =>
    (typeof wert === 'string' && ISO_TAG.test(wert.trim()) ? wert.trim() : null);

/** Nicht-negative ganze Zahl; alles Unbrauchbare wird 0. */
const zahl = (wert: unknown): number =>
    (typeof wert === 'number' && Number.isFinite(wert) ? Math.max(0, Math.round(wert)) : 0);

const jaNein = (wert: unknown): boolean => wert === true;

/** Erfasst heißt: da steht etwas, das nicht nur aus Leerzeichen besteht. */
const erfasst = (wert: unknown): boolean => text(wert, 4000).length > 0;

/** Liste von Freitexten, entleert und gekappt. */
const texte = (wert: unknown, max: number, maxLaenge: number = MAX_TEXTLAENGE): string[] =>
    (Array.isArray(wert) ? wert.map(w => text(w, maxLaenge)).filter(Boolean).slice(0, max) : []);

const liste = <T>(wert: unknown): T[] => (Array.isArray(wert) ? wert as T[] : []);

// ═══════════════════════════════════════════════════════════════════════════
// 1b) PERSONENBEZOGENES AUS DEM FREITEXT ENTFERNEN
// ═══════════════════════════════════════════════════════════════════════════
//
// WARUM FILTERN UND NICHT WEGLASSEN. Regeln und Notfallschritte könnten wie
// `zugang` und `sammelplatz` als bloße Anzahl mitgehen — dann wüsste der
// Assistent aber nur, DASS zehn Regeln erfasst sind, und nicht WELCHE. Er
// müsste raten, was fehlt, und würde Vorhandenes wiederholen; das ist genau
// die Antwort, die niemand prüfen mag. Der Wortlaut trägt hier den Nutzen.
//
// Er trägt aber auch das Risiko: eine Baustellenordnung ist die Stelle, an der
// Telefonnummern und Namen im Fließtext landen. Die Zusage im Dateikopf wäre
// ohne Filter schlicht unwahr.
//
// DER FILTER IST BEWUSST GROB. Ein Filter, der zu wenig fängt, ist schlimmer
// als keiner: er erzeugt Vertrauen, das er nicht deckt. Deshalb wird im
// Zweifel ersetzt. Der Preis ist gering — der Assistent braucht aus diesen
// Zeilen nur das THEMA („Ersthelfer verständigen"), nicht die Rufnummer. Eine
// versehentlich getroffene Uhrzeit oder Bestellnummer kostet nichts.
//
// WAS ER NICHT KANN: einen bloßen Nachnamen ohne Anrede und ohne Rolle
// („Kollegen Schmidt informieren") erkennt er nicht — dafür gibt es kein
// Muster, das nicht die halbe deutsche Substantivsprache mitnähme. Der Filter
// ist die zweite Verteidigungslinie; die erste bleibt, dass Kontaktdaten
// überhaupt nicht in dieser Datei ankommen (Notrufplan, Ersthelferliste).

const PLATZHALTER_TELEFON = '[Telefonnummer entfernt]';
const PLATZHALTER_MAIL = '[E-Mail entfernt]';
const PLATZHALTER_NAME = '[Name entfernt]';

/** E-Mail-Adressen, mit und ohne `mailto:`. */
const MAIL = /(?:mailto:)?[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-zÄÖÜäöü]{2,}/g;

/**
 * Eine Ziffernfolge mit den in Rufnummern üblichen Trennern. Was davon
 * tatsächlich ersetzt wird, entscheidet erst die Ersetzungsfunktion anhand
 * der Ziffernzahl — der Doppelpunkt fehlt in der Zeichenklasse mit Absicht,
 * damit „07:00–16:00" gar nicht erst als eine Folge gelesen wird.
 */
const ZIFFERNFOLGE = /\+?\d[\d\s()/.-]{3,}\d/g;

/** Reine Datumsangaben bleiben stehen — sie sind keine Erreichbarkeit. */
const NUR_DATUM = /^\s*(?:\d{1,2}\.\d{1,2}\.\d{2,4}|\d{4}-\d{1,2}-\d{1,2})\s*$/;

/**
 * Regelwerksnummern sehen aus wie Rufnummern („DGUV Information 201-011",
 * „DIN EN 12811-1"). In einer Baustellenordnung stehen sie ständig, und sie
 * zu schwärzen nähme dem Assistenten genau die Angabe, an der er erkennt,
 * welche Regel schon erfasst ist. Erkannt werden sie am Wort DAVOR — „Telefon"
 * gehört bewusst NICHT dazu.
 */
const REGELWERK_DAVOR =
    /\b(?:DGUV|DIN|EN|ISO|ASR|TRBS|TRGS|BGV|BGR|VDE|VDI|Vorschrift|Regel|Information|Grundsatz|Norm|§)\b\s*(?:[A-Za-z]{1,4}\b\s*)?$/i;

/** Anrede oder Titel plus ein bis zwei großgeschriebene Wörter. */
const ANREDE_NAME =
    /\b(?:Herrn?|Frau|Hr\.|Fr\.|Dr\.|Prof\.|Dipl\.-Ing\.)\s+[A-ZÄÖÜ][A-Za-zÄÖÜäöüß.'-]*(?:\s+[A-ZÄÖÜ][A-Za-zÄÖÜäöüß.'-]*)?/g;

/**
 * Rolle plus großgeschriebener Name („Ersthelfer Schmidt", „Bauleiter Meier").
 * Die Rolle bleibt stehen: sie ist die Information, der Name ist es nicht.
 */
const ROLLE_NAME =
    /\b(Ersthelfer(?:in)?|Brandschutzhelfer(?:in)?|Sicherheitsbeauftragte[rn]?|SiGeKo|Bauleiter(?:in)?|Polier(?:in)?|Vorarbeiter(?:in)?|Verantwortliche[rn]?|Ansprechpartner(?:in)?|Betriebsarzt|Betriebsärztin|Durchgangsarzt|D-Arzt|Notfallbeauftragte[rn]?)\b\s*[:,-]?\s+([A-ZÄÖÜ][a-zäöüß]+(?:\s+[A-ZÄÖÜ][a-zäöüß]+)?)/g;

/**
 * Großgeschriebene Wörter, die hinter einer Rolle regelmäßig KEIN Name sind.
 * Ohne diese Liste würde aus „Ersthelfer Erste Hilfe leisten" ein
 * „Ersthelfer [Name entfernt] leisten" — der Satz verlöre seinen Sinn, ohne
 * dass irgendetwas geschützt wäre.
 */
const KEIN_NAME = new Set([
    'Erste', 'Ersthelfer', 'Notruf', 'Notrufnummer', 'Rufnummer', 'Nummer', 'Name', 'Namen',
    'Rettungsdienst', 'Feuerwehr', 'Polizei', 'Krankenhaus', 'Sammelplatz', 'Baustelle',
    'Bauleitung', 'Bauherr', 'Aushang', 'Liste', 'Verzeichnis', 'Kontakt', 'Anwesenheit',
    'Sofort', 'Unverzüglich', 'Immer', 'Nur', 'Bei', 'Der', 'Die', 'Das', 'Ein', 'Eine',
    'Und', 'Oder', 'Alle', 'Jeder', 'Kein', 'Keine', 'Nach', 'Vor', 'Über', 'Unter',
]);

/**
 * Eine Freitextzeile ohne Telefonnummern, E-Mail-Adressen und erkennbare
 * Personennamen. Ersetzt statt zu löschen: eine Zeile, in der sichtbar etwas
 * entfernt wurde, ist immer noch prüfbar — eine stillschweigend gekürzte
 * nicht.
 */
export const ohnePersonendaten = (wert: string): string => {
    if (typeof wert !== 'string' || !wert) return '';
    return wert
        .replace(MAIL, PLATZHALTER_MAIL)
        .replace(ZIFFERNFOLGE, (treffer: string, versatz: number, ganz: string) => {
            const ziffern = (treffer.match(/\d/g) || []).length;
            // Unter sechs Ziffern ist es keine Erreichbarkeit, sondern der
            // Notruf 112, ein Regelwerk („DGUV Vorschrift 38") oder eine
            // Mengenangabe.
            if (ziffern < 6) return treffer;
            if (NUR_DATUM.test(treffer)) return treffer;
            // Regelwerksnummern nur bis acht Ziffern verschonen: darüber ist es
            // keine Norm mehr, sondern mit hoher Wahrscheinlichkeit doch eine
            // Rufnummer, und im Zweifel wird geschwärzt.
            if (ziffern <= 8 && REGELWERK_DAVOR.test(ganz.slice(0, versatz))) return treffer;
            return PLATZHALTER_TELEFON;
        })
        .replace(ANREDE_NAME, PLATZHALTER_NAME)
        .replace(ROLLE_NAME, (treffer, rolle: string, name: string) => {
            const erstes = name.split(/\s+/)[0];
            if (KEIN_NAME.has(erstes)) return treffer;
            return `${rolle} ${PLATZHALTER_NAME}`;
        });
};

/** Wie `texte`, aber jede Zeile geht zuerst durch `ohnePersonendaten`. */
const texteOhnePersonendaten = (wert: unknown, max: number, maxLaenge: number): string[] =>
    texte(
        liste<unknown>(wert).map(w => (typeof w === 'string' ? ohnePersonendaten(w) : w)),
        max,
        maxLaenge,
    );

/** Dreistufige Priorität wie im ganzen Haus (`Gefaehrdung.prioritaet`). */
export type Prioritaet = 'hoch' | 'mittel' | 'niedrig';

/**
 * Priorität aus einer KI-Antwort.
 *
 * Unbekanntes wird `mittel` und weder `hoch` noch `niedrig`. `hoch` würde
 * einen unverstandenen Vorschlag nach oben in den Plan schieben, `niedrig`
 * würde ihn verstecken — beides wäre eine Aussage, die das Modell nicht
 * getroffen hat.
 */
const prioritaet = (wert: unknown): Prioritaet => {
    const w = typeof wert === 'string' ? wert.trim().toLowerCase() : '';
    if (w === 'hoch' || w === 'high') return 'hoch';
    if (w === 'niedrig' || w === 'low' || w === 'gering') return 'niedrig';
    return 'mittel';
};

// ═══════════════════════════════════════════════════════════════════════════
// 2) EINGABEFORMEN — schmal und eigen
// ═══════════════════════════════════════════════════════════════════════════
//
// Bewusst eigene, schmale Formen und NICHT `SigeProject`, `Arbeitsschritt`,
// `KoordinierungPaar` oder `Handbuch`: dieselbe Überlegung wie bei
// `handbuch/types.ts:PlanSchritt`. Die Logik bleibt ohne den ganzen Planzweig
// testbar, und sie kann nichts anfassen, was ihr nicht gegeben wurde. Der
// Aufrufer reicht die echten Zeilen strukturell passend hinein; alles, was
// darüber hinaus an den Objekten hängt, fällt hier heraus.

/** Projektrahmen. Keine Adresse, kein Bauherr, kein Kunde. */
export interface ProjektEingabe {
    /** Art des Bauvorhabens („Neubau Bürogebäude"), NICHT seine Anschrift. */
    bauvorhaben?: string | null;
    bauzeitVon?: string | null;
    bauzeitBis?: string | null;
    hauptabschnitte?: string[] | null;
}

/** Ein Arbeitsschritt des SiGe-Plans, soweit der Assistent ihn braucht. */
export interface SchrittEingabe {
    bezeichnung?: string | null;
    gewerk?: string | null;
    zeitraum_von?: string | null;
    zeitraum_bis?: string | null;
    hauptabschnitt?: string | null;
    kritisch?: boolean | null;
    /** Bezeichnungen der bereits erfassten Gefährdungen — damit der Assistent
     *  sagen kann, was FEHLT, statt Vorhandenes zu wiederholen. */
    gefaehrdungen?: (string | null)[] | null;
}

/** Ein Katalogvorschlag, wie ihn `vorschlaegeFuerPaar` liefert. */
export interface KatalogVorschlagEingabe {
    code?: string | null;
    gefaehrdung?: string | null;
    massnahme?: string | null;
    prioritaet?: string | null;
}

/** Ein Gewerkepaar mit gerechneter Überschneidung. */
export interface PaarEingabe {
    aLabel?: string | null;
    bLabel?: string | null;
    /** `false` heißt: der Regelkatalog kennt dieses Gewerk nicht. */
    aZugeordnet?: boolean | null;
    bZugeordnet?: boolean | null;
    von?: string | null;
    bis?: string | null;
    dauerTage?: number | null;
    katalogVorschlaege?: KatalogVorschlagEingabe[] | null;
}

/** Ein Gewerk, das die Normalisierung nicht zuordnen konnte. */
export interface NichtZugeordnetesGewerkEingabe {
    label?: string | null;
    anzahlSchritte?: number | null;
    von?: string | null;
    bis?: string | null;
}

/** Stand des Handbuchs. Ohne Gewerkeseiten-Kontakte, ohne Nachweisdokumente. */
export interface HandbuchEingabe {
    /** `einzelvergabe` | `gu` | `gue` | `tu` | null. Als Text, damit diese
     *  Datei die Aufzählung des Handbuchmoduls nicht ein zweites Mal führt. */
    orgModell?: string | null;
    ordnung?: {
        arbeitszeiten?: string | null;
        zugang?: string | null;
        regeln?: (string | null)[] | null;
        notfallschritte?: (string | null)[] | null;
        sammelplatz?: string | null;
    } | null;
    /** `handbuecher.einrichtung` — die fünf Freitexte zum BE-Plan. */
    einrichtung?: {
        versorgung?: string | null;
        entsorgung?: string | null;
        lagerplatz?: string | null;
        verkehrsfuehrung?: string | null;
        bemerkung?: string | null;
    } | null;
    /**
     * Die Gewerke der Planfassung — Anzeigenamen.
     *
     * Sie gehen ausdrücklich MIT: ein Vorschlag zur Ver- und Entsorgung, der
     * nicht weiß, ob auf dieser Baustelle Asbest saniert oder nur gemalert
     * wird, ist ein Textbaustein und keine Aussage über dieses Projekt.
     */
    gewerke?: (string | null)[] | null;
    /** Bezeichnungen der gemeinsam genutzten Einrichtungen aus dem SiGe-Plan. */
    gemeinsameEinrichtungen?: (string | null)[] | null;
    /** Liegt im Projekt ein Lageplan? Nur Ja/Nein — das Bild geht nie mit. */
    lageplanVorhanden?: boolean | null;
    unvollstaendigeKapitel?: {
        kapitel?: string | null;
        titel?: string | null;
        fehlend?: (string | null)[] | null;
    }[] | null;
    /** Anzeigenamen der Gewerke ohne einen einzigen vorhandenen Nachweis. */
    gewerkeOhneNachweise?: (string | null)[] | null;
    nachweise?: {
        gesamt?: number | null;
        fehlt?: number | null;
        ueberfaellig?: number | null;
        ohneBeleg?: number | null;
    } | null;
}

/**
 * Die Planungsphase, soweit die Assistenten sie brauchen. `Planungsphase`
 * selbst passt strukturell hinein; alles Weitere an ihr (Status, Fragen,
 * Prüfung, Zeitstempel) fällt hier heraus.
 */
export interface PlanungEingabe {
    baubeschreibung?: string | null;
    bauaufgabe?: string | null;
    merkmale?: ProjektMerkmale | null;
}

// ═══════════════════════════════════════════════════════════════════════════
// 3) KONTEXTFORMEN — genau das, was das Haus verlässt
// ═══════════════════════════════════════════════════════════════════════════

export interface Bauzeit {
    von: string | null;
    bis: string | null;
}

/** Stand eines Merkmals der Planungsphase — dreiwertig wie dort. */
export type MerkmalStand = 'ja' | 'nein' | 'ungeklaert';

/**
 * Was die Planungsphase den Assistenten sagt.
 *
 * Die Merkmale stehen als drei Listen und nicht als Ja/Nein-Feld je Merkmal:
 * ein fehlender Schlüssel in `ProjektMerkmale` heißt „noch nicht beantwortet",
 * und als `false` gelesen würde daraus eine Entwarnung, die niemand gegeben
 * hat — derselbe Fehler, den die Planungsphase mit `PflichtStand` bei sich
 * selbst beseitigt hat. `hinweis` sagt dem Modell genau das; er steht IM
 * Objekt, damit er nicht in jedem der drei Proxy-Prompts einzeln gepflegt
 * werden muss (der Kontext wird dort als JSON durchgereicht).
 */
export interface PlanungKontext {
    hinweis: string;
    // Baubeschreibung und Bauaufgabe gehen bewusst NICHT mit: Freitext nennt
    // oft Bauherr und Anschrift („Bauherr Max Mustermann, Musterstraße 12"),
    // und `ohnePersonendaten` erkennt weder die Rolle ohne Anrede noch
    // Anschriften. Bis es eine verlässliche Bereinigung gibt, nur die
    // strukturierten Angaben.
    /** Wortlaut der Merkmale aus `PLANUNG_MERKMALE`, je nach Stand. */
    merkmale: { ja: string[]; nein: string[]; ungeklaert: string[] };
    /** Nur Zahl oder `null` — keine Schwellen, keine Bewertung. */
    zahlen: {
        arbeitstage: number | null;
        maxBeschaeftigte: number | null;
        bestandBaujahr: number | null;
        grabentiefeM: number | null;
        absturzhoeheM: number | null;
    };
}

export interface KontextSchritt {
    bezeichnung: string;
    gewerk: string;
    von: string | null;
    bis: string | null;
    hauptabschnitt: string;
    kritisch: boolean;
    gefaehrdungen: string[];
    /** Wieviele Gefährdungen über `MAX_GEFAEHRDUNGEN_JE_SCHRITT` hinaus da
     *  sind. 0 heißt: die Liste ist vollständig. */
    weitereGefaehrdungen: number;
}

export interface PlanAssistKontext {
    bauvorhaben: string;
    bauzeit: Bauzeit;
    /** Angaben der Planungsphase; `null`, wenn das Projekt keine hat. */
    planung: PlanungKontext | null;
    hauptabschnitte: string[];
    /** Alle im Plan vorkommenden Gewerke, in Reihenfolge des ersten Auftretens. */
    gewerke: string[];
    arbeitsschritte: KontextSchritt[];
    anzahlSchritte: number;
    /** Schritte ohne verwertbaren Zeitraum — die stillste Lücke im Plan. */
    schritteOhneZeitraum: number;
    /** Nicht mitgeschickte Schritte. > 0 heißt: die Liste ist unvollständig. */
    weitereSchritte: number;
}

export interface KontextPaar {
    a: string;
    b: string;
    von: string | null;
    bis: string | null;
    dauerTage: number | null;
    /** Welche der beiden der Regelkatalog kennt. */
    zugeordnet: { a: boolean; b: boolean };
    /** Was der Katalog zu diesem Paar schon sagt — damit die KI ERGÄNZT,
     *  statt dasselbe noch einmal zu sagen. */
    katalogGefaehrdungen: { code: string; gefaehrdung: string; massnahme: string; prioritaet: Prioritaet }[];
}

export interface KoordinierungAssistKontext {
    bauvorhaben: string;
    bauzeit: Bauzeit;
    /** Angaben der Planungsphase; `null`, wenn das Projekt keine hat. */
    planung: PlanungKontext | null;
    /** Fassung des Regelkatalogs, mit der die Vorschläge gerechnet wurden. */
    katalogVersion: string;
    paare: KontextPaar[];
    weiterePaare: number;
    /**
     * Gewerke, die die Normalisierung NICHT zuordnen konnte.
     *
     * Sie gehen ausdrücklich mit. Zu ihnen schweigt der Regelkatalog
     * vollständig — ein nicht zugeordnetes Gewerk ist aber kein Nachweis der
     * Gefährdungsfreiheit. Genau hier kann die zweite Stimme etwas beitragen,
     * das der Katalog strukturell nicht kann.
     */
    nichtZugeordneteGewerke: { label: string; anzahlSchritte: number; von: string | null; bis: string | null }[];
}

export interface HandbuchAssistKontext {
    bauvorhaben: string;
    bauzeit: Bauzeit;
    /** Angaben der Planungsphase; `null`, wenn das Projekt keine hat. */
    planung: PlanungKontext | null;
    orgModell: string;
    /** Gibt es überhaupt eine Baustellenordnung mit Inhalt? */
    ordnungErfasst: boolean;
    ordnung: {
        /** Nur Ja/Nein: die drei Freitexte können eine Anschrift tragen. */
        arbeitszeitenErfasst: boolean;
        zugangErfasst: boolean;
        sammelplatzErfasst: boolean;
        /** Wortlaut, damit der Vorschlag nicht wiederholt, was schon dasteht —
         *  jede Zeile durch `ohnePersonendaten` gelaufen (Rufnummern, Mails,
         *  Namen sind ersetzt). */
        regeln: string[];
        notfallschritte: string[];
    };
    /**
     * Der Stand der Baustelleneinrichtung.
     *
     * Der WORTLAUT geht mit und nicht bloß ein Ja/Nein — anders als bei den
     * drei Ordnungsfeldern oben. Der Grund ist der Zweck: der Assistent soll
     * hier ERGÄNZEN, was fehlt, und dafür muss er lesen, was schon dasteht.
     * Ein Vorschlag, der die vorhandene Zeile wiederholt, macht auf dem Blatt
     * denselben Satz zweimal. Jedes Feld läuft durch `ohnePersonendaten` —
     * „Ansprechpartner Herr Müller, 0170…" steht erfahrungsgemäß genau in
     * diesen Freitexten.
     */
    einrichtung: {
        versorgung: string;
        entsorgung: string;
        lagerplatz: string;
        verkehrsfuehrung: string;
        bemerkung: string;
    };
    /** Anzeigenamen der Gewerke aus der Planfassung. */
    gewerke: string[];
    /** Bezeichnungen der gemeinsam genutzten Einrichtungen des SiGe-Plans. */
    gemeinsameEinrichtungen: string[];
    lageplanVorhanden: boolean;
    unvollstaendigeKapitel: { kapitel: string; titel: string; fehlend: string[] }[];
    gewerkeOhneNachweise: string[];
    nachweise: { gesamt: number; fehlt: number; ueberfaellig: number; ohneBeleg: number };
}

// ═══════════════════════════════════════════════════════════════════════════
// 4) KONTEXTBAU
// ═══════════════════════════════════════════════════════════════════════════

const bauzeit = (projekt?: ProjektEingabe | null): Bauzeit => ({
    von: datum(projekt?.bauzeitVon),
    bis: datum(projekt?.bauzeitBis),
});

// ─── Planungsphase ──────────────────────────────────────────────────────────

/**
 * Die Merkmale der Planungsphase mit Bedeutung für Gefährdungen, in der
 * Reihenfolge, in der sie im Kontext erscheinen — und ihr Wortlaut für das
 * Modell. Die Schlüssel aus `ProjektMerkmale` allein („tga",
 * „gefahrstoffeAnhang2") sagen ihm nichts.
 *
 * NICHT dabei: `personentage` (eine Umfangsgröße der Schwellenwertprüfung,
 * keine Gefährdung) und die Zahlenfelder — die gehen unter `zahlen` als Zahl
 * mit, nicht als Ja/Nein. Ebenfalls nicht dabei: `dritterNachPara4` (Name und
 * Anschrift einer Person — Personendaten verlassen das Haus nicht) und
 * `gebaeudetyp` (kein Ja/Nein; die Liste führt nur dreiwertige Merkmale). Die
 * Wortlaute nennen bewusst keine Vorschrift: der Kontext soll das Modell
 * nicht dazu bringen, welche zu zitieren.
 */
export const PLANUNG_MERKMALE: ReadonlyArray<readonly [keyof ProjektMerkmale, string]> = [
    // Nicht nur gleichzeitig: auch nacheinander taetige Firmen zaehlen — ein
    // Nein der KI auf „gleichzeitig" blendete sonst Fragen aus (Review 01.11.).
    ['mehrereArbeitgeber', 'Beschäftigte mehrerer Arbeitgeber tätig, gleichzeitig oder nacheinander (auch Nachunternehmer, Montage durch Lieferanten, Eigenleistungen des Bauherrn)'],
    ['unternehmerOhneBeschaeftigte', 'Unternehmer ohne Beschäftigte (Solo-Selbstständige) tätig'],
    ['bestand', 'Bauen im Bestand (Umbau, Sanierung, Anbau, Rückbau)'],
    ['abbruch', 'Abbruch- oder Rückbauarbeiten'],
    ['erdarbeiten', 'Erdarbeiten, Baugrube, Gräben'],
    ['altlastenverdacht', 'Altlasten-, Bodenkontaminations- oder Deponieverdacht im Baufeld'],
    ['strassenbaustelle', 'Arbeiten im öffentlichen Verkehrsraum'],
    ['betriebLaeuftWeiter', 'Gebäude oder Teile bleiben während der Bauzeit in Nutzung'],
    ['fertigteilmontage', 'Montage von Massivbauelementen oder Fertigteilen mit Hebezeug'],
    ['kran', 'Turmdrehkran oder Autokran im Einsatz'],
    ['geruest', 'Gerüst erforderlich'],
    ['ortbeton', 'Ortbeton-, Schal- und Bewehrungsarbeiten'],
    ['dacharbeiten', 'Dacharbeiten'],
    ['freileitung', 'Hochspannungsfreileitung in der Nähe'],
    ['gewaesser', 'Arbeiten an oder über Wasser'],
    ['tunnelbau', 'Brunnenbau, unterirdische Erdarbeiten oder Tunnelbau'],
    ['taucharbeiten', 'Taucherarbeiten'],
    ['druckluft', 'Arbeiten in Druckluft'],
    ['sprengarbeiten', 'Sprengarbeiten'],
    ['strahlung', 'Ionisierende Strahlung mit Kontroll- oder Überwachungsbereich'],
    ['gefahrstoffeAnhang2', 'Stoffe explosiv, krebserzeugend oder biologische Arbeitsstoffe der Risikogruppe 3/4'],
    ['tga', 'Technische Gebäudeausrüstung in nennenswertem Umfang'],
    ['nachtarbeit', 'Nacht-, Schicht- oder Wochenendarbeit'],
    ['winterbau', 'Winterbauphase'],
];

/** Steht im Kontext neben den Merkmalen — siehe `PlanungKontext.hinweis`. */
export const PLANUNG_HINWEIS =
    'Angaben des Koordinators aus der Planungsphase. "ungeklaert" heißt: noch nicht '
    + 'beantwortet — das ist NICHT "nein"; behandle ein ungeklärtes Merkmal als möglich. '
    + 'Zahlen ohne Angabe sind null.';

/** `true` → ja, `false` → nein, alles andere (auch fehlend) → ungeklärt. */
const merkmalStand = (wert: unknown): MerkmalStand =>
    (wert === true ? 'ja' : wert === false ? 'nein' : 'ungeklaert');

/** Eine endliche Zahl oder `null` — ohne Rundung: 5,5 m Grabentiefe sind 5,5 m. */
const zahlOderNull = (wert: unknown): number | null =>
    (typeof wert === 'number' && Number.isFinite(wert) ? wert : null);

/**
 * Baustein „Planungsphase" für alle drei Assistenten und den UfsA-Katalog.
 *
 * `null` ohne Phase — und zwar als Wert, nicht als fehlender Schlüssel: das
 * Modell soll lesen, DASS es keine Planungsphase gibt, und nicht raten. Eine
 * angelegte Phase ohne eine einzige Antwort ergibt dagegen ein Objekt mit
 * lauter „ungeklaert" — ebenfalls eine Aussage, und zwar eine andere.
 *
 * Baubeschreibung und Bauaufgabe sind Freitexte des Koordinators; dort steht
 * erfahrungsgemäß auch mal „Bauherr Max Mustermann, Musterstraße 12". Weil
 * `ohnePersonendaten` weder die Rolle ohne Anrede noch Anschriften erkennt,
 * gehen beide NICHT mit — nur Merkmale und Zahlen.
 */
export const planungKontext = (phase?: PlanungEingabe | null): PlanungKontext | null => {
    if (!phase || typeof phase !== 'object') return null;
    const m: Partial<Record<keyof ProjektMerkmale, unknown>> =
        phase.merkmale && typeof phase.merkmale === 'object' ? phase.merkmale : {};

    const merkmale: PlanungKontext['merkmale'] = { ja: [], nein: [], ungeklaert: [] };
    for (const [schluessel, wortlaut] of PLANUNG_MERKMALE) {
        merkmale[merkmalStand(m[schluessel])].push(wortlaut);
    }

    return {
        hinweis: PLANUNG_HINWEIS,
        merkmale,
        zahlen: {
            arbeitstage: zahlOderNull(m.arbeitstage),
            maxBeschaeftigte: zahlOderNull(m.maxBeschaeftigte),
            bestandBaujahr: zahlOderNull(m.bestandBaujahr),
            grabentiefeM: zahlOderNull(m.grabentiefeM),
            absturzhoeheM: zahlOderNull(m.absturzhoeheM),
        },
    };
};

/**
 * Kontext für `assistSigePlan` — der Assistent im Plan-Reiter.
 *
 * Er beantwortet Fragen zum Plan ALS GANZES und ersetzt
 * `generateSigePlanHazards` nicht: dort geht es um den Gefährdungskatalog zu
 * einem Arbeitsschritt, hier um das, was zwischen den Schritten fehlt.
 * Deshalb gehen die Schritte in Breite (alle, mit Zeitraum und Gewerk), aber
 * ohne Tiefe (keine Schutzmaßnahmen, keine Vorschriftenlisten) mit.
 *
 * `planung` ist optional: Aufrufer ohne Planungsphase bleiben unverändert und
 * schicken `planung: null`.
 */
export const baueSigePlanKontext = (
    projekt?: ProjektEingabe | null,
    schritte?: (SchrittEingabe | null)[] | null,
    planung?: PlanungEingabe | null,
): PlanAssistKontext => {
    const alle = liste<SchrittEingabe | null>(schritte).filter(Boolean) as SchrittEingabe[];

    const gewerke: string[] = [];
    for (const s of alle) {
        const g = text(s.gewerk, 120);
        if (g && !gewerke.includes(g)) gewerke.push(g);
    }

    const gewaehlt = alle.slice(0, MAX_SCHRITTE);
    const arbeitsschritte: KontextSchritt[] = gewaehlt.map(s => {
        const alleGef = texte(s.gefaehrdungen, Number.MAX_SAFE_INTEGER, 200);
        return {
            bezeichnung: text(s.bezeichnung, 200),
            gewerk: text(s.gewerk, 120),
            von: datum(s.zeitraum_von),
            bis: datum(s.zeitraum_bis),
            hauptabschnitt: text(s.hauptabschnitt, 120),
            kritisch: jaNein(s.kritisch),
            gefaehrdungen: alleGef.slice(0, MAX_GEFAEHRDUNGEN_JE_SCHRITT),
            weitereGefaehrdungen: Math.max(0, alleGef.length - MAX_GEFAEHRDUNGEN_JE_SCHRITT),
        };
    });

    return {
        bauvorhaben: text(projekt?.bauvorhaben, 200),
        bauzeit: bauzeit(projekt),
        // Vor den Schritten: der Proxy kappt das JSON notfalls hinten, und die
        // Planungsangaben sollen das nicht als Erstes treffen.
        planung: planungKontext(planung),
        hauptabschnitte: texte(projekt?.hauptabschnitte, MAX_LISTE, 120),
        gewerke: gewerke.slice(0, MAX_LISTE * 2),
        arbeitsschritte,
        anzahlSchritte: alle.length,
        schritteOhneZeitraum: alle.filter(s => !datum(s.zeitraum_von) || !datum(s.zeitraum_bis)).length,
        weitereSchritte: Math.max(0, alle.length - gewaehlt.length),
    };
};

/**
 * Kontext für `assistKoordinierung` — die zweite Stimme NEBEN dem Regelkatalog.
 *
 * ═══ WARUM DER KATALOG DIE GRUNDLAGE BLEIBT ════════════════════════════════
 * Zwei Gründe, die eine KI-Antwort strukturell nicht erfüllen kann:
 *   1. Der Katalog rechnet OHNE NETZ. Auf der Baustelle ist keins — der
 *      Koordinierungsteil muss dort vollständig arbeiten, wo er gebraucht wird.
 *   2. Seine FASSUNG steht am Datensatz (`katalog_version`). Damit bleibt in
 *      einem abgeschlossenen Projekt nachvollziehbar, warum eine Gefährdung
 *      dort stand. Eine KI-Antwort ist nicht reproduzierbar und trägt keine
 *      Fassung; sie kann denselben Nachweis nicht führen.
 * Deshalb geht das, was der Katalog bereits sagt, als „schon gesagt" MIT —
 * die KI ergänzt und überschreibt nicht.
 */
export const baueKoordinierungKontext = (
    projekt?: ProjektEingabe | null,
    paare?: (PaarEingabe | null)[] | null,
    nichtZugeordnet?: (NichtZugeordnetesGewerkEingabe | null)[] | null,
    katalogVersion?: string | number | null,
    planung?: PlanungEingabe | null,
): KoordinierungAssistKontext => {
    const alle = liste<PaarEingabe | null>(paare).filter(Boolean) as PaarEingabe[];
    const gewaehlt = alle.slice(0, MAX_PAARE);

    const kontextPaare: KontextPaar[] = gewaehlt.map(p => ({
        a: text(p.aLabel, 120),
        b: text(p.bLabel, 120),
        von: datum(p.von),
        bis: datum(p.bis),
        dauerTage: typeof p.dauerTage === 'number' && Number.isFinite(p.dauerTage) ? p.dauerTage : null,
        zugeordnet: { a: jaNein(p.aZugeordnet), b: jaNein(p.bZugeordnet) },
        katalogGefaehrdungen: liste<KatalogVorschlagEingabe>(p.katalogVorschlaege)
            .slice(0, MAX_KATALOG_JE_PAAR)
            .map(v => ({
                code: text(v?.code, 40),
                gefaehrdung: text(v?.gefaehrdung),
                massnahme: text(v?.massnahme),
                prioritaet: prioritaet(v?.prioritaet),
            })),
    }));

    return {
        bauvorhaben: text(projekt?.bauvorhaben, 200),
        bauzeit: bauzeit(projekt),
        planung: planungKontext(planung),
        katalogVersion: text(katalogVersion == null ? '' : String(katalogVersion), 40),
        paare: kontextPaare,
        weiterePaare: Math.max(0, alle.length - gewaehlt.length),
        nichtZugeordneteGewerke: (liste<NichtZugeordnetesGewerkEingabe | null>(nichtZugeordnet)
            .filter(Boolean) as NichtZugeordnetesGewerkEingabe[])
            .slice(0, MAX_LISTE * 2)
            .map(g => ({
                label: text(g.label, 120),
                anzahlSchritte: zahl(g.anzahlSchritte),
                von: datum(g.von),
                bis: datum(g.bis),
            })),
    };
};

/**
 * Kontext für `assistHandbuch`.
 *
 * Die drei Freitexte der Baustellenordnung, die eine Anschrift oder einen
 * Treffpunkt tragen können (`arbeitszeiten` ist harmlos, `zugang` und
 * `sammelplatz` sind es nicht), gehen NUR als Ja/Nein mit — und
 * `arbeitszeiten` aus Gleichlauf ebenso. Für die Aufgabe reicht das
 * vollständig: der Assistent soll VORSCHLAGEN, was fehlt, und nicht
 * beurteilen, was dasteht. Regeln und Notfallschritte gehen im Wortlaut mit,
 * damit der Vorschlag nicht wiederholt, was schon erfasst ist — aber jede
 * Zeile erst durch `ohnePersonendaten`: „Ersthelfer Herr Schmidt (0171
 * 2345678) verständigen" ist derselbe Inhalt, den der Notrufplan sperrt, und
 * er darf nicht durch die Hintertür einer Regelzeile hinausgehen.
 */
export const baueHandbuchKontext = (
    projekt?: ProjektEingabe | null,
    stand?: HandbuchEingabe | null,
    planung?: PlanungEingabe | null,
): HandbuchAssistKontext => {
    const ordnung = stand?.ordnung || null;
    const regeln = texteOhnePersonendaten(ordnung?.regeln, MAX_LISTE, 200);
    const notfallschritte = texteOhnePersonendaten(ordnung?.notfallschritte, MAX_LISTE, 200);
    const nachweise = stand?.nachweise || null;
    const einr = stand?.einrichtung || null;
    /** Ein Einrichtungsfeld, entpersonalisiert und gekappt. */
    const feld = (wert: unknown): string =>
        text(typeof wert === 'string' ? ohnePersonendaten(wert) : wert, 600);

    return {
        bauvorhaben: text(projekt?.bauvorhaben, 200),
        bauzeit: bauzeit(projekt),
        planung: planungKontext(planung),
        orgModell: text(stand?.orgModell, 40),
        ordnungErfasst: erfasst(ordnung?.arbeitszeiten) || erfasst(ordnung?.zugang)
            || erfasst(ordnung?.sammelplatz) || regeln.length > 0 || notfallschritte.length > 0,
        ordnung: {
            arbeitszeitenErfasst: erfasst(ordnung?.arbeitszeiten),
            zugangErfasst: erfasst(ordnung?.zugang),
            sammelplatzErfasst: erfasst(ordnung?.sammelplatz),
            regeln,
            notfallschritte,
        },
        einrichtung: {
            versorgung: feld(einr?.versorgung),
            entsorgung: feld(einr?.entsorgung),
            lagerplatz: feld(einr?.lagerplatz),
            verkehrsfuehrung: feld(einr?.verkehrsfuehrung),
            bemerkung: feld(einr?.bemerkung),
        },
        gewerke: texte(stand?.gewerke, MAX_LISTE * 2, 120),
        gemeinsameEinrichtungen: texte(stand?.gemeinsameEinrichtungen, MAX_LISTE, 120),
        lageplanVorhanden: jaNein(stand?.lageplanVorhanden),
        unvollstaendigeKapitel: liste<Record<string, unknown> | null>(stand?.unvollstaendigeKapitel)
            .slice(0, MAX_LISTE)
            .map(k => ({
                kapitel: text(k?.kapitel, 60),
                titel: text(k?.titel, 120),
                fehlend: texte(k?.fehlend, MAX_LISTE, 200),
            })),
        gewerkeOhneNachweise: texte(stand?.gewerkeOhneNachweise, MAX_LISTE * 2, 120),
        nachweise: {
            gesamt: zahl(nachweise?.gesamt),
            fehlt: zahl(nachweise?.fehlt),
            ueberfaellig: zahl(nachweise?.ueberfaellig),
            ohneBeleg: zahl(nachweise?.ohneBeleg),
        },
    };
};

// ═══════════════════════════════════════════════════════════════════════════
// 5) ANTWORTFORMEN
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Ergebnis eines Assistenten-Aufrufs.
 *
 * `ok: false` heißt: es gibt NICHTS zu zeigen — `daten` ist die leere Form,
 * `fehler` der Satz für die Oberfläche. Halbe Daten stillschweigend
 * durchzureichen ist der Fehler, den dieser Umschlag verhindert.
 *
 * `unvollstaendig: true` heißt: die Antwort war am Token-Limit abgeschnitten
 * und wurde bis zum letzten vollständigen Eintrag gerettet. Das ist echte
 * Beute — aber die Oberfläche MUSS es sagen, sonst hält jemand eine halbe
 * Liste für die ganze.
 *
 * `verworfen` nennt die Felder, die bei dieser Rettung KOMPLETT unter den
 * Tisch gefallen sind. Sie sind in `daten` leer — aber leer aus einem ganz
 * anderen Grund als „die KI hatte nichts zu sagen". Ohne diese Liste kann die
 * Oberfläche beides nicht unterscheiden und meldet Entwarnung, wo ein Teil
 * der Antwort verloren ging. Bei `ok: true` immer zusammen mit
 * `unvollstaendig: true`.
 */
export interface AiAssistErgebnis<T> {
    ok: boolean;
    daten: T;
    unvollstaendig: boolean;
    fehler?: string;
    /** Feldnamen aus `daten`, die die Rettung nicht mitnehmen konnte. */
    verworfen?: string[];
}

export interface FehlenderArbeitsschritt {
    bezeichnung: string;
    gewerk: string;
    hauptabschnitt: string;
    begruendung: string;
}

export interface FehlendeGefaehrdung {
    /** Wortgetreu einer der übergebenen Schritte, oder leer für planweit. */
    arbeitsschritt: string;
    gefaehrdung: string;
    massnahme: string;
    prioritaet: Prioritaet;
}

export interface PlanLuecke {
    thema: string;
    beschreibung: string;
    schweregrad: 'hinweis' | 'warnung' | 'fehler';
}

export interface PlanAssistAntwort {
    antwort: string;
    fehlendeArbeitsschritte: FehlenderArbeitsschritt[];
    fehlendeGefaehrdungen: FehlendeGefaehrdung[];
    luecken: PlanLuecke[];
}

/**
 * Ein ergänzender Vorschlag der KI zu einem Gewerkepaar.
 *
 * Feldschnitt wie beim Katalogvorschlag (`koordinierungLogic.Vorschlag`),
 * damit die Oberfläche beides in derselben Liste zeigen kann — aber
 * GEKENNZEICHNET: `quelle: 'ki'` ist der Wert, den auch die Datenbankspalte
 * `koordinierung_paare.quelle` kennt. Ein KI-Vorschlag trägt bewusst KEINEN
 * Katalogcode: er ist keine Fassung, auf die man sich später berufen kann.
 */
export interface KoordinierungKiVorschlag {
    /** Gewerkename wortgetreu aus dem Kontext — sonst ist der Vorschlag
     *  keinem Paar zuzuordnen und wird verworfen. */
    a: string;
    b: string;
    gefaehrdung: string;
    massnahme: string;
    prioritaet: Prioritaet;
    /** Räumliche Voraussetzung, falls das Modell eine nennt. `null` heißt
     *  „nicht gesagt" und wird NICHT zu „gilt überall" ergänzt. */
    raumbezug: 'uebereinander' | 'gleicher_bereich' | 'benachbart' | 'baustellenweit' | null;
    /** Nur gesicherte Fundstellen; im Zweifel leer. */
    vorschriften: string[];
    begruendung: string;
    quelle: 'ki';
}

export interface KoordinierungAssistAntwort {
    vorschlaege: KoordinierungKiVorschlag[];
    hinweise: string[];
}

/** Rohtext für die Baustellenordnung — Feldschnitt wie `Baustellenordnung`. */
export interface OrdnungVorschlag {
    arbeitszeiten: string;
    zugang: string;
    regeln: string[];
    notfallschritte: string[];
    sammelplatz: string;
}

/**
 * Rohtext für die Baustelleneinrichtung — Feldschnitt wie
 * `Baustelleneinrichtung` im Handbuchmodul.
 *
 * Getrennt von `OrdnungVorschlag` und nicht mit ihm verschmolzen: die beiden
 * werden EINZELN übernommen. Wer nur die Einrichtung ergänzen lassen will,
 * darf sich nicht nebenbei die Baustellenordnung überschreiben — und
 * umgekehrt.
 */
export interface EinrichtungVorschlag {
    versorgung: string;
    entsorgung: string;
    lagerplatz: string;
    verkehrsfuehrung: string;
    bemerkung: string;
}

export interface HandbuchAssistAntwort {
    ordnung: OrdnungVorschlag;
    einrichtung: EinrichtungVorschlag;
    fehlendeAngaben: { kapitel: string; hinweis: string }[];
    hinweise: string[];
}

// ═══════════════════════════════════════════════════════════════════════════
// 6) ANTWORTEN LESEN UND PRÜFEN
// ═══════════════════════════════════════════════════════════════════════════

/** Satz für die Oberfläche, wenn gar nichts zu retten war. */
export const KEINE_ANTWORT =
    'Die KI hat keine verwertbare Antwort geliefert. Bitte noch einmal versuchen; '
    + 'übernommen wird ohnehin nichts automatisch.';

/**
 * Satz für den Körper, der zwar ein Objekt ist, aber keines der erwarteten
 * Felder trägt — der Fall `{}`.
 *
 * ZWEITE REIHE hinter dem Proxy. Der Proxy setzte bei einer blockierten
 * Antwort (finishReason SAFETY/RECITATION, leere candidates) ein leeres Objekt
 * ab und antwortete mit HTTP 200; hier unten kam damit ein gültiger Körper mit
 * lauter leeren Listen an, und die Oberfläche meldete in ruhigem Grau „nichts
 * zu ergänzen gefunden". Ein blockierter Aufruf sah aus wie ein geprüftes
 * Ergebnis ohne Befund — in einem Arbeitsschutz-Werkzeug die eine Falschaussage,
 * die man nicht ausliefert.
 *
 * Der Proxy behandelt das inzwischen selbst als Fehler. Diese Prüfung bleibt
 * trotzdem: Proxy und Client werden GETRENNT ausgerollt, und bis zum Deploy
 * der Edge-Funktion ist sie die einzige Reihe, die steht.
 */
export const ANTWORT_OHNE_FELDER =
    'Die KI hat geantwortet, aber ohne die erwarteten Felder — vermutlich wurde die Anfrage '
    + 'abgewiesen. Das ist ein Fehlversuch und kein „nichts gefunden". Bitte noch einmal versuchen.';

export const LEERE_PLAN_ANTWORT: PlanAssistAntwort = {
    antwort: '',
    fehlendeArbeitsschritte: [],
    fehlendeGefaehrdungen: [],
    luecken: [],
};

export const LEERE_KOORDINIERUNG_ANTWORT: KoordinierungAssistAntwort = {
    vorschlaege: [],
    hinweise: [],
};

export const LEERE_HANDBUCH_ANTWORT: HandbuchAssistAntwort = {
    ordnung: { arbeitszeiten: '', zugang: '', regeln: [], notfallschritte: [], sammelplatz: '' },
    einrichtung: { versorgung: '', entsorgung: '', lagerplatz: '', verkehrsfuehrung: '', bemerkung: '' },
    fehlendeAngaben: [],
    hinweise: [],
};

/**
 * Rettet EINE benannte Liste aus einer abgeschnittenen Antwort.
 *
 * Der Namensnachweis vorweg ist zwingend: `salvageJsonArray` fällt ohne
 * Treffer auf das ERSTE Objekt-Array im Text zurück. Bei einer Antwort mit
 * mehreren Listen (Plan-Assistent: drei) lieferte es dann für jeden Schlüssel
 * dieselbe erste Liste — die fehlenden Arbeitsschritte stünden anschließend
 * als fehlende Gefährdungen im Plan. Deshalb: kein Schlüssel im Text, keine
 * Rettung.
 */
const rette = (rohText: string, schluessel: string): Record<string, unknown>[] => {
    const gefunden = new RegExp(`["']${schluessel}["']\\s*:\\s*\\[`, 'i').test(rohText);
    if (!gefunden) return [];
    return salvageJsonArray(rohText, [schluessel])
        .filter(x => !!x && typeof x === 'object' && !Array.isArray(x)) as Record<string, unknown>[];
};

/** Der geparste Antwortkörper, oder `null`, wenn keiner vorliegt. */
const koerper = (roh: unknown): Record<string, unknown> | null =>
    (roh && typeof roh === 'object' && !Array.isArray(roh) ? roh as Record<string, unknown> : null);

/**
 * Trägt der Körper wenigstens EINES der erwarteten Felder?
 *
 * Geprüft wird die Anwesenheit des Schlüssels, nicht sein Inhalt: `{"luecken":
 * []}` ist eine echte Aussage („keine Lücken gefunden") und bleibt gültig.
 * `{}` oder `{"_truncated": true}` sind dagegen keine Aussage — dort hat das
 * Modell nichts geantwortet, was zur Frage gehört, und das darf nie als leeres
 * Ergebnis durchgehen.
 */
const hatEinesDerFelder = (k: Record<string, unknown>, felder: readonly string[]): boolean =>
    felder.some(f => Object.prototype.hasOwnProperty.call(k, f));

const PLAN_FELDER = ['antwort', 'fehlendeArbeitsschritte', 'fehlendeGefaehrdungen', 'luecken'] as const;
const KOORDINIERUNG_FELDER = ['vorschlaege', 'hinweise'] as const;
const HANDBUCH_FELDER = ['ordnung', 'einrichtung', 'fehlendeAngaben', 'hinweise'] as const;

const eintraege = (wert: unknown): Record<string, unknown>[] =>
    liste<unknown>(wert)
        .filter(x => !!x && typeof x === 'object' && !Array.isArray(x))
        .slice(0, MAX_ANTWORT_EINTRAEGE) as Record<string, unknown>[];

const schweregrad = (wert: unknown): PlanLuecke['schweregrad'] => {
    const w = typeof wert === 'string' ? wert.trim().toLowerCase() : '';
    if (w === 'fehler') return 'fehler';
    if (w === 'warnung') return 'warnung';
    return 'hinweis';
};

const RAUMBEZUEGE = ['uebereinander', 'gleicher_bereich', 'benachbart', 'baustellenweit'] as const;

const raumbezug = (wert: unknown): KoordinierungKiVorschlag['raumbezug'] => {
    const w = typeof wert === 'string' ? wert.trim().toLowerCase() : '';
    return (RAUMBEZUEGE as readonly string[]).includes(w)
        ? w as KoordinierungKiVorschlag['raumbezug']
        : null;
};

const alsSchritt = (e: Record<string, unknown>): FehlenderArbeitsschritt => ({
    bezeichnung: text(e.bezeichnung, 200),
    gewerk: text(e.gewerk, 120),
    hauptabschnitt: text(e.hauptabschnitt, 120),
    begruendung: text(e.begruendung),
});

const alsGefaehrdung = (e: Record<string, unknown>): FehlendeGefaehrdung => ({
    arbeitsschritt: text(e.arbeitsschritt, 200),
    gefaehrdung: text(e.gefaehrdung),
    massnahme: text(e.massnahme),
    prioritaet: prioritaet(e.prioritaet),
});

const alsLuecke = (e: Record<string, unknown>): PlanLuecke => ({
    thema: text(e.thema, 200),
    beschreibung: text(e.beschreibung),
    schweregrad: schweregrad(e.schweregrad),
});

/**
 * Liest die Antwort von `assistSigePlan`.
 *
 * Reihenfolge der Prüfung ist wichtig: die Abschneide-Marke wird VOR dem
 * Auspacken gelesen (`isTruncatedAiResponse`), weil sie sonst genau in dem
 * Fall verlorengeht, für den sie gebaut wurde.
 */
export const lesePlanAssistAntwort = (roh: unknown): AiAssistErgebnis<PlanAssistAntwort> => {
    const abgeschnitten = isTruncatedAiResponse(roh);
    const rohText = rawAiText(roh);

    // Ein gefülltes `text`-Feld heißt: der Proxy konnte die Antwort nicht als
    // JSON lesen. Dann bleibt die Rettung bis zum letzten vollständigen Eintrag.
    if (rohText) {
        const daten: PlanAssistAntwort = {
            antwort: '',
            fehlendeArbeitsschritte: rette(rohText, 'fehlendeArbeitsschritte')
                .slice(0, MAX_ANTWORT_EINTRAEGE).map(alsSchritt),
            fehlendeGefaehrdungen: rette(rohText, 'fehlendeGefaehrdungen')
                .slice(0, MAX_ANTWORT_EINTRAEGE).map(alsGefaehrdung),
            luecken: rette(rohText, 'luecken').slice(0, MAX_ANTWORT_EINTRAEGE).map(alsLuecke),
        };
        const etwas = daten.fehlendeArbeitsschritte.length + daten.fehlendeGefaehrdungen.length
            + daten.luecken.length;
        // `antwort` ist bei der Rettung nicht zu haben (sie ist ein Feld, kein
        // Array) — das wird gemeldet und nicht als „die KI hat nichts gesagt"
        // ausgegeben.
        return etwas > 0
            ? { ok: true, daten, unvollstaendig: true, verworfen: ['antwort'] }
            : { ok: false, daten: LEERE_PLAN_ANTWORT, unvollstaendig: abgeschnitten, fehler: KEINE_ANTWORT };
    }

    const k = koerper(roh);
    if (!k) return { ok: false, daten: LEERE_PLAN_ANTWORT, unvollstaendig: abgeschnitten, fehler: KEINE_ANTWORT };
    // Körper ohne ein einziges erwartetes Feld: Fehlversuch, kein leeres Ergebnis.
    if (!hatEinesDerFelder(k, PLAN_FELDER)) {
        return { ok: false, daten: LEERE_PLAN_ANTWORT, unvollstaendig: abgeschnitten, fehler: ANTWORT_OHNE_FELDER };
    }

    return {
        ok: true,
        unvollstaendig: abgeschnitten,
        daten: {
            antwort: text(k.antwort, 1200),
            fehlendeArbeitsschritte: eintraege(k.fehlendeArbeitsschritte).map(alsSchritt),
            fehlendeGefaehrdungen: eintraege(k.fehlendeGefaehrdungen).map(alsGefaehrdung),
            luecken: eintraege(k.luecken).map(alsLuecke),
        },
    };
};

/**
 * Ein KI-Vorschlag ohne Gewerkepaar oder ohne Gefährdungstext wird VERWORFEN.
 *
 * Er ließe sich keinem Paar zuordnen. Die Oberfläche müsste ihn dann irgendwo
 * hinschreiben — und „irgendwo" heißt im SiGe-Plan: bei den falschen beiden
 * Gewerken.
 */
const alsKiVorschlag = (e: Record<string, unknown>): KoordinierungKiVorschlag | null => {
    const a = text(e.a ?? e.gewerkA, 120);
    const b = text(e.b ?? e.gewerkB, 120);
    const gefaehrdung = text(e.gefaehrdung);
    const massnahme = text(e.massnahme);
    if (!a || !b || !gefaehrdung || !massnahme) return null;
    return {
        a,
        b,
        gefaehrdung,
        massnahme,
        prioritaet: prioritaet(e.prioritaet),
        raumbezug: raumbezug(e.raumbezug),
        vorschriften: texte(e.vorschriften, 6, 120),
        begruendung: text(e.begruendung),
        quelle: 'ki',
    };
};

/** Liest die Antwort von `assistKoordinierung`. */
export const leseKoordinierungAntwort = (roh: unknown): AiAssistErgebnis<KoordinierungAssistAntwort> => {
    const abgeschnitten = isTruncatedAiResponse(roh);
    const rohText = rawAiText(roh);

    if (rohText) {
        const vorschlaege = rette(rohText, 'vorschlaege')
            .slice(0, MAX_ANTWORT_EINTRAEGE)
            .map(alsKiVorschlag)
            .filter(Boolean) as KoordinierungKiVorschlag[];
        return vorschlaege.length > 0
            ? { ok: true, daten: { vorschlaege, hinweise: [] }, unvollstaendig: true, verworfen: ['hinweise'] }
            : { ok: false, daten: LEERE_KOORDINIERUNG_ANTWORT, unvollstaendig: abgeschnitten, fehler: KEINE_ANTWORT };
    }

    const k = koerper(roh);
    if (!k) return { ok: false, daten: LEERE_KOORDINIERUNG_ANTWORT, unvollstaendig: abgeschnitten, fehler: KEINE_ANTWORT };
    if (!hatEinesDerFelder(k, KOORDINIERUNG_FELDER)) {
        return { ok: false, daten: LEERE_KOORDINIERUNG_ANTWORT, unvollstaendig: abgeschnitten, fehler: ANTWORT_OHNE_FELDER };
    }

    return {
        ok: true,
        unvollstaendig: abgeschnitten,
        daten: {
            vorschlaege: eintraege(k.vorschlaege).map(alsKiVorschlag).filter(Boolean) as KoordinierungKiVorschlag[],
            hinweise: texte(k.hinweise, MAX_ANTWORT_EINTRAEGE),
        },
    };
};

/** Liest die Antwort von `assistHandbuch`. */
export const leseHandbuchAntwort = (roh: unknown): AiAssistErgebnis<HandbuchAssistAntwort> => {
    const abgeschnitten = isTruncatedAiResponse(roh);
    const rohText = rawAiText(roh);

    if (rohText) {
        // `ordnung` und `einrichtung` sind OBJEKTE und keine Listen — dafür
        // gibt es keine Rettung bis zum letzten vollständigen Eintrag. Ein
        // halb gelesener Ordnungstext wäre außerdem das Schlechteste, was man
        // aushängen kann, und eine halbe Einrichtungszeile („Bauschutt wird
        // getrennt in") steht anschließend genauso im Handbuch.
        //
        // Sie wird also VERWORFEN, und genau das muss mitgehen: eine leere
        // `ordnung` aus diesem Zweig heißt „ging verloren" und NICHT „dort ist
        // nichts zu ergänzen". Ohne die Unterscheidung meldet die Oberfläche
        // Entwarnung für einen Teil der Antwort, den sie nie gesehen hat —
        // derselbe Fehler wie beim leeren Objekt weiter oben, nur eine Etage
        // tiefer. `unvollstaendig` steht hier deshalb fest auf `true` und
        // hängt nicht an der Abschneide-Marke des Proxys.
        const fehlendeAngaben = rette(rohText, 'fehlendeAngaben')
            .slice(0, MAX_ANTWORT_EINTRAEGE)
            .map(e => ({ kapitel: text(e.kapitel, 60), hinweis: text(e.hinweis) }))
            .filter(e => !!e.hinweis);
        return fehlendeAngaben.length > 0
            ? {
                ok: true,
                daten: { ...LEERE_HANDBUCH_ANTWORT, fehlendeAngaben },
                unvollstaendig: true,
                verworfen: ['ordnung', 'einrichtung', 'hinweise'],
            }
            : { ok: false, daten: LEERE_HANDBUCH_ANTWORT, unvollstaendig: abgeschnitten, fehler: KEINE_ANTWORT };
    }

    const k = koerper(roh);
    if (!k) return { ok: false, daten: LEERE_HANDBUCH_ANTWORT, unvollstaendig: abgeschnitten, fehler: KEINE_ANTWORT };
    if (!hatEinesDerFelder(k, HANDBUCH_FELDER)) {
        return { ok: false, daten: LEERE_HANDBUCH_ANTWORT, unvollstaendig: abgeschnitten, fehler: ANTWORT_OHNE_FELDER };
    }

    const o = koerper(k.ordnung) || {};
    const e = koerper(k.einrichtung) || {};
    return {
        ok: true,
        unvollstaendig: abgeschnitten,
        daten: {
            ordnung: {
                arbeitszeiten: text(o.arbeitszeiten, 600),
                zugang: text(o.zugang, 600),
                regeln: texte(o.regeln, MAX_ANTWORT_EINTRAEGE, 300),
                notfallschritte: texte(o.notfallschritte, MAX_ANTWORT_EINTRAEGE, 300),
                sammelplatz: text(o.sammelplatz, 300),
            },
            // Gekappt bei 600 Zeichen und damit deutlich unter dem, was die
            // Kapitelspalte drucken kann. Die Grenze ist Absicht: was das
            // Modell darüber hinaus schreibt, würde auf dem A3-Blatt ohnehin
            // abgeschnitten (`overflow: hidden`), und ein Vorschlag, dessen
            // Ende nur im Formular steht, ist im Handbuch eine halbe Aussage.
            einrichtung: {
                versorgung: text(e.versorgung, 600),
                entsorgung: text(e.entsorgung, 600),
                lagerplatz: text(e.lagerplatz, 600),
                verkehrsfuehrung: text(e.verkehrsfuehrung, 600),
                bemerkung: text(e.bemerkung, 600),
            },
            fehlendeAngaben: eintraege(k.fehlendeAngaben)
                .map(e => ({ kapitel: text(e.kapitel, 60), hinweis: text(e.hinweis) }))
                .filter(e => !!e.hinweis),
            hinweise: texte(k.hinweise, MAX_ANTWORT_EINTRAEGE),
        },
    };
};

// ═══════════════════════════════════════════════════════════════════════════
// 7) BAUSTELLENORDNUNG (Modul 06) — `assistBauordnung`
// ═══════════════════════════════════════════════════════════════════════════
//
// Der vierte Assistent, gebaut nach `docs/modul-baustellenordnung-spec.md`
// Abschnitt F. Er ergänzt eine Baustellenordnung um das, was Katalog und
// Handbuch nicht hergeben — einmal je Fassung, automatisch nach dem
// Automatik-Lauf. Seine Vorschläge werden als Regeln mit `quelle: 'ki'` und
// OHNE `bestaetigt_am` gespeichert; ins Dokument kommen sie erst, wenn ein
// Mensch sie bestätigt.
//
// Zwei Dinge sind hier strenger als bei den drei Assistenten oben, beide aus
// Spec F.5:
//
//   1. KEINE ZAHLEN AUS DEM PROJEKT. Merkmale gehen nur als ja/nein/unbekannt
//      mit. `planungKontext` wird deshalb NICHT weitergereicht: es trägt
//      Grabentiefe, Absturzhöhe und Beschäftigtenzahl als Zahl, und aus einer
//      Zahl im Kontext baut das Modell eine Grenzwertaussage. Außerdem wäre
//      es eine zweite Merkmalsquelle neben `merkmale`, die dem eingefrorenen
//      Stand der Fassung widersprechen kann.
//   2. DER LESER VERWIRFT, statt nur zu kappen. Ein Vorschlag mit Zahl und
//      Einheit, Fundstelle, Regelwerkskürzel, Rechtsfolge oder Ausnahmeformel
//      geht nicht durch, ebenso einer mit erfundener Kapitelkennung — und
//      jeder verworfene wird GEZÄHLT. Eine still gekürzte Antwort wäre nicht
//      prüfbar (dieselbe Überlegung wie bei `ohnePersonendaten`).
//
// Der Grund für die Strenge ist kein formaler: der SiGeKo-Vertrag hat
// Schutzwirkung zugunsten aller, die sich berechtigt auf der Baustelle
// aufhalten. Ein generierter Fehler in einer Baustellenordnung trifft
// unmittelbar Dritte.

/** Titel der Katalogbausteine je Kapitel im Kontext (Spec F.2). */
export const MAX_BAUSTEINE_JE_KAPITEL = 12;
/** Bestätigte Regelungen je Kapitel im Kontext (Spec F.2). */
export const MAX_REGELN_JE_KAPITEL = 20;
/** Vorschläge je Antwort — dieselbe Zahl, die der Proxy-Prompt nennt. */
export const MAX_BAUORDNUNG_VORSCHLAEGE = 30;
/** Sätze zur Gesamtlage (Spec F.3). */
export const MAX_BAUORDNUNG_HINWEISE = 3;
/**
 * Zeichen des serialisierten Kontexts.
 *
 * Der Proxy schneidet bei 12.000 Zeichen blind ab — als Notnagel, und ein dort
 * abgeschnittenes JSON ist unlesbar. Eine Baustellenordnung mit neun Kapiteln
 * und je zwanzig bestätigten Regelungen läge weit darüber. Deshalb wird HIER
 * gekürzt, gleichmäßig über die Kapitel und mit Vermerk in `gekappt`, bis der
 * Kontext mit Abstand unter die Grenze des Proxys passt.
 */
export const MAX_BAUORDNUNG_KONTEXT_ZEICHEN = 11000;

/** Länger ist keine Kennung mehr (`kapitelKennungAus` bleibt weit darunter). */
const MAX_KENNUNG = 80;

/** Ein gezogenes Kapitel, wie der Aufrufer es hineinreicht. */
export interface BauordnungKapitelEingabe {
    id: string;
    titel: string;
    dokument: 'ordnung' | 'hse';
    pflicht: boolean;
}

/**
 * Was der Aufrufer für `assistBauordnung` hineinreicht.
 *
 * Wie bei den anderen Eingaben eine eigene, schmale Form — nicht das Bundle,
 * nicht die Regelzeilen, nicht die Organisation. Sonst hinge die
 * Datensparsamkeit an der Sorgfalt jedes Aufrufers.
 */
export interface BauordnungKontextEingabe {
    projekt?: ProjektEingabe | null;
    /**
     * Wird angenommen, aber NICHT weitergereicht (siehe Abschnittskopf): ihr
     * Kontext trüge Zahlen. Gelesen wird nur, OB es eine Planungsphase gibt.
     */
    planung?: PlanungEingabe | null;
    /** Ja/Nein/unbekannt je Merkmal. Zahlenmerkmale gehen NICHT mit (F.2). */
    merkmale: ProjektMerkmale;
    orgModell: string | null;
    /** Die gezogenen Kapitel — beider Dokumente, wenn der HSE-Plan an ist. */
    kapitel: BauordnungKapitelEingabe[];
    /**
     * Bestätigte Wortlaute je Kapitelkennung — NUR frei formulierte und
     * frühere KI-Regeln. Katalog- und Handbuchregeln bleiben im Haus: ihre
     * gefüllten Platzhalter tragen Anschriften, Rufnummern und Stellen
     * (`dialogLogik.kiKontextEingabe`, Codex-Review 02.11.2026).
     */
    regelnJeKapitel: Record<string, string[]>;
    /** Zahl ALLER bestätigten Regelungen je Kapitelkennung — damit das Modell weiß, dass ein Kapitel geregelt ist, ohne die Texte zu sehen. */
    regelnAnzahlJeKapitel?: Record<string, number>;
    /** Titel (nie Wortlaut) der Katalogbausteine je Kapitelkennung. */
    bausteinTitelJeKapitel: Record<string, string[]>;
    /** Nur Funktion oder Rolle („sigeko_ausfuehrung"), nie Name, Firma, Erreichbarkeit. */
    rollenFunktionen: string[];
    /** Nur OB etwas eingetragen ist — der Wortlaut kann eine Anschrift sein. */
    erfasst: { geltungsort: boolean; sammelplatz: boolean; zugang: boolean };
    gewerke: string[];
}

/** Stand eines Merkmals, wie das Modell ihn liest. */
export type BauordnungMerkmalStand = 'ja' | 'nein' | 'unbekannt';

export interface BauordnungKontextKapitel {
    /** Wortgetreu wie hineingereicht — das Modell muss sie unverändert zurückgeben. */
    kennung: string;
    titel: string;
    /** Die Dokumente, die das Kapitel drucken. */
    dokumente: ('ordnung' | 'hse')[];
    pflicht: boolean;
    /** Frei formulierte bestätigte Regelungen im Wortlaut, jede durch `ohnePersonendaten`, ohne Platzhalter-Marken. */
    regeln: string[];
    /** Zahl aller bestätigten Regelungen des Kapitels (Katalog, Handbuch, frei, KI) — mindestens `regeln.length`. */
    regelnGesamt: number;
    /** Nur die Titel der Katalogbausteine — ihr Wortlaut bleibt im Haus (Spec E.2). */
    bausteinTitel: string[];
}

export interface BauordnungAssistKontext {
    /** Lesehilfe für das Modell — steht IM Objekt wie `PlanungKontext.hinweis`. */
    hinweis: string;
    bauvorhaben: string;
    bauzeit: Bauzeit;
    orgModell: string;
    /** Gibt es eine Planungsphase? Ohne sie sind in der Regel alle Merkmale unbekannt. */
    planungVorhanden: boolean;
    /** Wortlaut der Ja/Nein-Merkmale aus `PLANUNG_MERKMALE`, nach Stand. */
    merkmale: Record<BauordnungMerkmalStand, string[]>;
    erfasst: { geltungsort: boolean; sammelplatz: boolean; zugang: boolean };
    rollen: string[];
    gewerke: string[];
    /**
     * Was gekürzt übermittelt wurde, ein Satz je Fall; leer heißt vollständig.
     * Dieselbe Aufgabe wie `weitereSchritte`: das Modell soll aus dem Schweigen
     * nicht auf Vollständigkeit schließen. Steht VOR den Kapiteln, damit ihn
     * auch ein Notschnitt im Proxy nicht trifft.
     */
    gekappt: string[];
    /** Zuletzt: schneidet der Proxy doch einmal ab, trifft es die Regeln. */
    kapitel: BauordnungKontextKapitel[];
}

/** Steht im Kontext — siehe `BauordnungAssistKontext.hinweis`. */
export const BAUORDNUNG_HINWEIS =
    'Merkmale: "unbekannt" heißt noch nicht beantwortet — das ist NICHT "nein". Zu einem '
    + 'unbekannten Merkmal schlägst du keine Regel vor; es wird gesondert geklärt. Zahlenwerte '
    + 'des Projekts werden bewusst nicht übermittelt. "erfasst" sagt nur, OB Geltungsort, '
    + 'Sammelplatz und Zugang eingetragen sind, nicht was dort steht. Je Kapitel nennt "regeln" '
    + 'nur die frei formulierten Regelungen im Wortlaut; "regelnGesamt" ist die Zahl ALLER '
    + 'bestätigten Regelungen des Kapitels, und "bausteinTitel" nennt die Katalogbausteine — '
    + 'was dort steht, gilt als geregelt und wird nicht erneut vorgeschlagen. "gekappt" nennt, '
    + 'was gekürzt übermittelt wurde.';

/** Platzhalter-Marken im Wortlaut — `[Aushangort]` wie `{sammelplatz}` — werden zu einer Auslassung. */
const ohnePlatzhalterMarken = (wortlaut: string): string =>
    wortlaut.replace(/\[[^\]\n]{1,60}\]|\{[a-z0-9_]+\}/g, '…');

/** Wortlaut je Merkmalsschlüssel — gepflegt an EINER Stelle, `PLANUNG_MERKMALE`. */
const MERKMAL_WORTLAUT: ReadonlyMap<string, string> =
    new Map(PLANUNG_MERKMALE.map(([schluessel, wortlaut]) => [schluessel as string, wortlaut]));

/**
 * Nur die Ja/Nein-Merkmale (`BOOLEAN_MERKMALE`), dreiwertig. Ein fehlender
 * Wert ist unbekannt und nicht „nein" — dieselbe Regel wie `merkmalStand`.
 * Die Zahlenmerkmale fallen hier heraus, weil die Liste sie nicht führt.
 */
const bauordnungMerkmale = (merkmale: unknown): Record<BauordnungMerkmalStand, string[]> => {
    const m: Record<string, unknown> =
        merkmale && typeof merkmale === 'object' ? merkmale as Record<string, unknown> : {};
    const ergebnis: Record<BauordnungMerkmalStand, string[]> = { ja: [], nein: [], unbekannt: [] };
    for (const schluessel of BOOLEAN_MERKMALE) {
        const stand = merkmalStand(m[schluessel]);
        ergebnis[stand === 'ungeklaert' ? 'unbekannt' : stand].push(MERKMAL_WORTLAUT.get(schluessel) ?? schluessel);
    }
    return ergebnis;
};

/** Eintrag eines Verzeichnisses — nur eine eigene Eigenschaft, nie `constructor` & Co. */
const eigenerEintrag = (verzeichnis: unknown, schluessel: string): unknown =>
    (verzeichnis && typeof verzeichnis === 'object' && Object.prototype.hasOwnProperty.call(verzeichnis, schluessel)
        ? (verzeichnis as Record<string, unknown>)[schluessel]
        : undefined);

/** Ohne Doppel, in Reihenfolge des ersten Auftretens. */
const ohneDoppel = (werte: string[]): string[] => [...new Set(werte)];

/**
 * Kontext für `assistBauordnung` (Spec F.2).
 *
 * Mit geht: Gewerke, die Ja/Nein-Merkmale dreiwertig, die gezogenen Kapitel
 * mit Kennung und Titel, je Kapitel die bestätigten Regelungen im Wortlaut
 * (durch `ohnePersonendaten`) und die Titel der Katalogbausteine, Rollen nur
 * als Funktion, Geltungsort/Sammelplatz/Zugang nur als ja/nein, das
 * Organisationsmodell. NICHT mit gehen: Zahlenwerte aus dem Projekt, der
 * Wortlaut von Geltungsort/Sammelplatz/Zugang, Namen, Firmen und
 * Erreichbarkeit hinter den Rollen, der Notrufplan.
 *
 * Ein Kapitel, das in beiden Dokumenten steht, geht EINMAL mit: Kapitel sind
 * dokumentneutral (`kapitel.ts`), und dieselben Regeln zweimal zu schicken
 * kostete nur Platz.
 */
export const baueBauordnungKontext = (e: BauordnungKontextEingabe): BauordnungAssistKontext => {
    const eingabe: Partial<BauordnungKontextEingabe> = e && typeof e === 'object' ? e : {};
    const festGekappt: string[] = [];

    // Gewerke sind Handbuch-Bezeichnungen — getippt, also koennen Firmen oder
    // Namen darin stehen: durch denselben Filter wie die Rollen.
    const gewerkeAlle = ohneDoppel(texteOhnePersonendaten(eingabe.gewerke, Number.MAX_SAFE_INTEGER, 120));
    const gewerke = gewerkeAlle.slice(0, MAX_SCHRITTE);
    if (gewerkeAlle.length > gewerke.length) {
        festGekappt.push(`gewerke: ${gewerkeAlle.length - gewerke.length} weitere nicht übermittelt`);
    }

    // Durch den Filter, obwohl nur Funktionen erwartet werden: „SiGeKo Herr
    // Meier" in einem Rollenfeld wäre genau der Name, der nicht hinausgeht.
    const rollenAlle = ohneDoppel(texteOhnePersonendaten(eingabe.rollenFunktionen, Number.MAX_SAFE_INTEGER, 80));
    const rollen = rollenAlle.slice(0, MAX_LISTE * 2);
    if (rollenAlle.length > rollen.length) {
        festGekappt.push(`rollen: ${rollenAlle.length - rollen.length} weitere nicht übermittelt`);
    }

    // Erst ungekappt sammeln; gekürzt wird unten, wenn feststeht, wieviel Platz ist.
    const kapitelVoll: BauordnungKontextKapitel[] = [];
    const nachKennung = new Map<string, BauordnungKontextKapitel>();
    for (const k of liste<Partial<BauordnungKapitelEingabe> | null>(eingabe.kapitel)) {
        if (!k || typeof k !== 'object') continue;
        // Die Kennung bleibt, wie sie kommt: der Leser vergleicht die Antwort
        // WORTGETREU mit den Kennungen des Aufrufers. Was keine Kennung sein
        // kann, geht gar nicht erst mit.
        const kennung = typeof k.id === 'string' ? k.id : '';
        if (!kennung.trim() || kennung.length > MAX_KENNUNG) continue;
        const dokument = k.dokument === 'ordnung' || k.dokument === 'hse' ? k.dokument : null;
        const vorhanden = nachKennung.get(kennung);
        if (vorhanden) {
            if (dokument && !vorhanden.dokumente.includes(dokument)) vorhanden.dokumente.push(dokument);
            vorhanden.pflicht = vorhanden.pflicht || jaNein(k.pflicht);
            continue;
        }
        const neu: BauordnungKontextKapitel = {
            kennung,
            // Eigene Kapitel tragen einen getippten Titel — durch den Filter.
            titel: text(typeof k.titel === 'string' ? ohnePersonendaten(k.titel) : '', 120),
            dokumente: dokument ? [dokument] : [],
            pflicht: jaNein(k.pflicht),
            regeln: texteOhnePersonendaten(
                // Platzhalter-Marken (`[Aushangort]`, `{sammelplatz}`) raus: sie
                // sind kein Inhalt, und ein gefüllter Wert gehört ohnehin nicht
                // in den Kontext — siehe `regelnJeKapitel`.
                liste<unknown>(eigenerEintrag(eingabe.regelnJeKapitel, kennung))
                    .map(w => (typeof w === 'string' ? ohnePlatzhalterMarken(w) : w)),
                Number.MAX_SAFE_INTEGER, MAX_TEXTLAENGE,
            ),
            regelnGesamt: 0,
            bausteinTitel: texte(eigenerEintrag(eingabe.bausteinTitelJeKapitel, kennung), Number.MAX_SAFE_INTEGER, 120),
        };
        const anzahl = eingabe.regelnAnzahlJeKapitel ? eigenerEintrag(eingabe.regelnAnzahlJeKapitel, kennung) : undefined;
        neu.regelnGesamt = Math.max(neu.regeln.length, Number.isFinite(Number(anzahl)) ? Math.max(0, Math.trunc(Number(anzahl))) : 0);
        nachKennung.set(kennung, neu);
        kapitelVoll.push(neu);
    }

    const geruest = {
        hinweis: BAUORDNUNG_HINWEIS,
        // Projektnamen tragen oft Bauherr oder Strasse — durch den Filter.
        bauvorhaben: text(typeof eingabe.projekt?.bauvorhaben === 'string' ? ohnePersonendaten(eingabe.projekt.bauvorhaben) : '', 200),
        bauzeit: bauzeit(eingabe.projekt),
        orgModell: text(eingabe.orgModell, 40),
        planungVorhanden: !!eingabe.planung && typeof eingabe.planung === 'object',
        merkmale: bauordnungMerkmale(eingabe.merkmale),
        erfasst: {
            geltungsort: jaNein(eingabe.erfasst?.geltungsort),
            sammelplatz: jaNein(eingabe.erfasst?.sammelplatz),
            zugang: jaNein(eingabe.erfasst?.zugang),
        },
        rollen,
        gewerke,
    };

    /** Der Kontext mit diesen Grenzen je Kapitel — und dem Vermerk, was dabei wegfiel. */
    const mitGrenzen = (regelGrenze: number, bausteinGrenze: number): BauordnungAssistKontext => {
        const gekappt = [...festGekappt];
        const kapitel = kapitelVoll.map(k => {
            const regeln = k.regeln.slice(0, regelGrenze);
            const bausteinTitel = k.bausteinTitel.slice(0, bausteinGrenze);
            if (k.regeln.length > regeln.length) {
                gekappt.push(`${k.kennung}: ${k.regeln.length - regeln.length} weitere bestätigte Regelungen nicht übermittelt`);
            }
            if (k.bausteinTitel.length > bausteinTitel.length) {
                gekappt.push(`${k.kennung}: ${k.bausteinTitel.length - bausteinTitel.length} weitere Bausteintitel nicht übermittelt`);
            }
            return { ...k, regeln, bausteinTitel };
        });
        return { ...geruest, gekappt, kapitel };
    };

    // Zuerst die Obergrenzen aus F.2. Passt das Ganze dann noch nicht unter
    // das Budget, wird je Kapitel GLEICHMÄSSIG weiter gekürzt — erst die
    // Regeln (lang, und das Modell soll sie ohnehin nur nicht wiederholen),
    // dann die Bausteintitel. Gleichmäßig, damit nicht die hinteren Kapitel
    // leer ausgehen, nur weil sie hinten stehen.
    let regelGrenze = MAX_REGELN_JE_KAPITEL;
    let bausteinGrenze = MAX_BAUSTEINE_JE_KAPITEL;
    let kontext = mitGrenzen(regelGrenze, bausteinGrenze);
    while (JSON.stringify(kontext).length > MAX_BAUORDNUNG_KONTEXT_ZEICHEN && (regelGrenze > 0 || bausteinGrenze > 0)) {
        if (regelGrenze > 0) regelGrenze -= 1;
        else bausteinGrenze -= 1;
        kontext = mitGrenzen(regelGrenze, bausteinGrenze);
    }
    return kontext;
};

// ─── Antwort ────────────────────────────────────────────────────────────────

/**
 * Ein Ergänzungsvorschlag der KI zur Baustellenordnung (Spec F.3).
 *
 * Kein `ausgabe`-Feld: mit dem Kapitel steht fest, welche Dokumente den Satz
 * drucken — das Modell entscheidet nicht über Dokumente. `quelle: 'ki'` ist
 * der Wert, den auch die Spalte `bauordnung_regeln.quelle` kennt.
 */
export interface BauordnungKiVorschlag {
    /** Wortgetreu eine Kennung aus dem Kontext — sonst verworfen. */
    kapitel: string;
    wortlaut: string;
    art: 'regel' | 'koordinierungshinweis';
    begruendung: string;
    quelle: 'ki';
}

export interface BauordnungAssistAntwort {
    vorschlaege: BauordnungKiVorschlag[];
    /** `kapitel` ist leer, wenn das Modell keine Kennung aus dem Kontext nannte. */
    fehlendeAngaben: { kapitel: string; hinweis: string }[];
    hinweise: string[];
}

export const LEERE_BAUORDNUNG_ANTWORT: BauordnungAssistAntwort = {
    vorschlaege: [],
    fehlendeAngaben: [],
    hinweise: [],
};

/**
 * Ergebnis von `leseBauordnungAntwort`.
 *
 * Nicht zu verwechseln mit `verworfen` aus `AiAssistErgebnis`: das nennt
 * FELDER, die bei der Rettung einer abgeschnittenen Antwort verloren gingen.
 * Hier geht es um einzelne VORSCHLÄGE, die gelesen und abgelehnt wurden.
 */
export interface BauordnungLeseErgebnis extends AiAssistErgebnis<BauordnungAssistAntwort> {
    /** Vorschläge, die der Leser verworfen hat — sichtbar zu melden (F.5). */
    verworfeneVorschlaege: number;
    /** Gründe je verworfenem Vorschlag, z. B. „Zahl mit Einheit", „Fundstelle", „Kapitel unbekannt". */
    verwerfungsgruende: string[];
}

/**
 * Die Sperre der Ebene 3 aus Spec F.5: ein Vorschlag, dessen Wortlaut eines
 * dieser Muster trifft, wird verworfen und gezählt.
 *
 * Vier Muster stehen wörtlich wie in der Spezifikation. Das erste weicht ab,
 * und zwar in Richtung „strenger":
 *   • Hinter der Einheit steht `(?![A-Za-zÄÖÜäöüß])` statt `\b`. Mit `\b`
 *     trafen „%" und „€" nie: zwischen zwei Nichtwortzeichen („50 % der",
 *     „50 %" am Satzende) liegt keine Wortgrenze, und „50 % der Fläche" ging
 *     durch.
 *   • Die ausgeschriebenen und gebeugten Formen derselben Einheiten zählen
 *     mit („2 Meter", „3 Tagen", „10 Prozent") — sonst ginge ein Grenzwert
 *     durch, sobald das Modell die Einheit ausschreibt. Dazu Minuten, Jahre
 *     und Grad: Fristen und Grenzwerte derselben Art.
 * Das Regelwerksmuster trifft das Kürzel auch ohne Nummer und auch die
 * gültigen Regelwerke — über die Spezifikation hinaus auch die Gesetzes- und
 * Verordnungskürzel (BaustellV, ArbSchG, BetrSichV …) und Normreihen (DIN,
 * VDE): Vorschriften kommen im Modul nur aus dem Bausteinfeld `vorschriften`,
 * nie aus einer KI-Antwort. Der Proxy führt `assistBauordnung` deshalb in
 * `OHNE_REGELWERKSPRUEFUNG`: liefe seine Bereinigung vorweg, striche sie den
 * Paragraphen heraus und der Rest des Satzes ginge hier durch.
 *
 * Kein Muster trägt das Flag `g` — `test` bleibt damit ohne Zustand.
 */
export const BAUORDNUNG_VERWERFUNGSMUSTER: ReadonlyArray<{ grund: string; muster: RegExp }> = Object.freeze([
    {
        grund: 'Zahl mit Einheit',
        muster: /\d+([.,]\d+)?\s*(m|cm|mm|kg|t|%|dB|EUR|€|h|Std|Tage?n?|Wochen?|Monate?n?|Meter|Metern|Zentimeter|Millimeter|Kilogramm|Tonnen?|Prozent|Dezibel|Euro|Stunden?|Min|Minuten?|Jahre?n?|Grad|°C)(?![A-Za-zÄÖÜäöüß])/i,
    },
    { grund: 'Fundstelle', muster: /§|Abs\.|Anhang\s+[IVX0-9]/ },
    {
        grund: 'Regelwerkskürzel',
        muster: /\b(DGUV|TRBS|TRGS|ASR|RAB|BGV|BGR|BGI|BGG|BaustellV|ArbSchG|BetrSichV|ArbStättV|ArbStaettV|GefStoffV|BioStoffV|LärmVibrationsArbSchV|ArbMedVV|PSA-BV|ArbZG|JArbSchG|MuSchG|BImSchG|TA\s+Lärm|GewAbfV|KrWG|WHG|StVO|LBO|BauO|MBO|VOB|BGB|SGB|DIN|VDE|VDI|ISO)\b/i,
    },
    { grund: 'Rechtsfolgensatz', muster: /\b(haftet|wird geahndet|ist befugt|ist verpflichtet, .* zu dulden)\b/i },
    { grund: 'Ausnahmeformel', muster: /\b(es sei denn|sofern .* nicht möglich|soweit zumutbar)\b/i },
]);

/** Gründe, die an keinem Muster hängen. */
const GRUND_UNLESBAR = 'unlesbar';
const GRUND_OHNE_WORTLAUT = 'ohne Wortlaut';
const GRUND_KAPITEL_UNBEKANNT = 'Kapitel unbekannt';
const GRUND_DOPPELT = 'doppelt';
const GRUND_HOECHSTZAHL = 'über der Höchstzahl';

const BAUORDNUNG_FELDER = ['vorschlaege', 'fehlendeAngaben', 'hinweise'] as const;

/** Nur die zwei Werte der Spalte `art`; Unbekanntes wird `regel` (die Vorbelegung, Spec E.3). */
const bauordnungArt = (wert: unknown): BauordnungKiVorschlag['art'] =>
    (typeof wert === 'string' && wert.trim().toLowerCase() === 'koordinierungshinweis' ? 'koordinierungshinweis' : 'regel');

/**
 * Prüft die Vorschlagsliste Eintrag für Eintrag.
 *
 * Gemustert wird der VOLLE Wortlaut und nicht der auf 300 Zeichen gekappte:
 * eine Zahl hinter der Kappung stünde zwar nicht im Vorschlag, der Satz davor
 * wäre aber derselbe unzulässige Satz, nur abgeschnitten. Die Höchstzahl gilt
 * für die ANGENOMMENEN Vorschläge — was ohnehin verworfen wird, verbraucht
 * keinen Platz.
 */
const pruefeBauordnungVorschlaege = (
    eintraegeRoh: unknown[],
    erlaubt: ReadonlySet<string>,
): { vorschlaege: BauordnungKiVorschlag[]; gruende: string[] } => {
    const vorschlaege: BauordnungKiVorschlag[] = [];
    const gruende: string[] = [];
    const gesehen = new Set<string>();
    for (const eintrag of eintraegeRoh) {
        const e = koerper(eintrag);
        if (!e) { gruende.push(GRUND_UNLESBAR); continue; }
        const wortlaut = text(e.wortlaut, 4000);
        if (!wortlaut) { gruende.push(GRUND_OHNE_WORTLAUT); continue; }
        // Wortgetreu: eine erfundene Kennung ist dieselbe Klasse Fehler wie
        // eine erfundene Zahl (Spec F.3) — kein Nachschlagen „ähnlicher" Kapitel.
        const kapitel = text(e.kapitel, MAX_KENNUNG);
        if (!erlaubt.has(kapitel)) { gruende.push(GRUND_KAPITEL_UNBEKANNT); continue; }
        const treffer = BAUORDNUNG_VERWERFUNGSMUSTER.find(m => m.muster.test(wortlaut));
        if (treffer) { gruende.push(treffer.grund); continue; }
        const schluessel = `${kapitel}\u0000${wortlaut.toLowerCase()}`;
        if (gesehen.has(schluessel)) { gruende.push(GRUND_DOPPELT); continue; }
        if (vorschlaege.length >= MAX_BAUORDNUNG_VORSCHLAEGE) { gruende.push(GRUND_HOECHSTZAHL); continue; }
        gesehen.add(schluessel);
        vorschlaege.push({
            kapitel,
            wortlaut: text(wortlaut, 300),
            art: bauordnungArt(e.art),
            begruendung: text(e.begruendung, 300),
            quelle: 'ki',
        });
    }
    return { vorschlaege, gruende };
};

/** Eine fehlende Angabe; eine unbekannte Kennung wird leer statt geraten. */
const alsFehlendeAngabe = (erlaubt: ReadonlySet<string>) =>
    (e: Record<string, unknown>): { kapitel: string; hinweis: string } => {
        const kapitel = text(e.kapitel, MAX_KENNUNG);
        return { kapitel: erlaubt.has(kapitel) ? kapitel : '', hinweis: text(e.hinweis, 300) };
    };

const bauordnungFehlschlag = (fehler: string, unvollstaendig: boolean): BauordnungLeseErgebnis => ({
    ok: false,
    daten: LEERE_BAUORDNUNG_ANTWORT,
    unvollstaendig,
    fehler,
    verworfeneVorschlaege: 0,
    verwerfungsgruende: [],
});

/**
 * Liest die Antwort von `assistBauordnung`.
 *
 * `erlaubteKapitel` sind die Kennungen, die im Kontext standen — gegen sie
 * wird jeder Vorschlag WORTGETREU geprüft.
 *
 * Eine leere Vorschlagsliste bei `ok: true` ist eine gültige Antwort („der
 * Assistent sieht nichts zu ergänzen"), ebenso eine, deren Vorschläge alle
 * verworfen wurden — dann steht die Zahl in `verworfeneVorschlaege`. `ok:
 * false` gibt es nur beim Fehlversuch: kein Körper, keines der Felder, oder
 * eine abgeschnittene Antwort, aus der nichts zu retten war (Spec F.4).
 */
export const leseBauordnungAntwort = (roh: unknown, erlaubteKapitel: readonly string[]): BauordnungLeseErgebnis => {
    const abgeschnitten = isTruncatedAiResponse(roh);
    const rohText = rawAiText(roh);
    const erlaubt: ReadonlySet<string> = new Set(
        (Array.isArray(erlaubteKapitel) ? erlaubteKapitel : []).filter(k => typeof k === 'string'),
    );

    if (rohText) {
        // Gerettet werden die beiden Listen, jede mit Namensnachweis (`rette`).
        // `hinweise` ist eine Liste von Texten und keine von Objekten — dafür
        // gibt es keine Rettung; sie wird als verloren gemeldet. Eine Liste,
        // aus der nichts zu retten war, ebenso: leer heißt hier „unbekannt",
        // nicht „nichts zu ergänzen".
        const { vorschlaege, gruende } = pruefeBauordnungVorschlaege(rette(rohText, 'vorschlaege'), erlaubt);
        const fehlendeAngaben = rette(rohText, 'fehlendeAngaben')
            .slice(0, MAX_ANTWORT_EINTRAEGE)
            .map(alsFehlendeAngabe(erlaubt))
            .filter(e => !!e.hinweis);
        const gelesen = vorschlaege.length + gruende.length;
        if (gelesen + fehlendeAngaben.length === 0) return bauordnungFehlschlag(KEINE_ANTWORT, abgeschnitten);
        return {
            ok: true,
            daten: { vorschlaege, fehlendeAngaben, hinweise: [] },
            unvollstaendig: true,
            verworfen: [
                ...(gelesen === 0 ? ['vorschlaege'] : []),
                ...(fehlendeAngaben.length === 0 ? ['fehlendeAngaben'] : []),
                'hinweise',
            ],
            verworfeneVorschlaege: gruende.length,
            verwerfungsgruende: gruende,
        };
    }

    const k = koerper(roh);
    if (!k) return bauordnungFehlschlag(KEINE_ANTWORT, abgeschnitten);
    // Körper ohne ein einziges erwartetes Feld: Fehlversuch, kein leeres Ergebnis.
    if (!hatEinesDerFelder(k, BAUORDNUNG_FELDER)) return bauordnungFehlschlag(ANTWORT_OHNE_FELDER, abgeschnitten);

    const { vorschlaege, gruende } = pruefeBauordnungVorschlaege(liste<unknown>(k.vorschlaege), erlaubt);
    return {
        ok: true,
        unvollstaendig: abgeschnitten,
        daten: {
            vorschlaege,
            fehlendeAngaben: eintraege(k.fehlendeAngaben)
                .map(alsFehlendeAngabe(erlaubt))
                .filter(e => !!e.hinweis),
            hinweise: texte(k.hinweise, MAX_BAUORDNUNG_HINWEISE, 300),
        },
        verworfeneVorschlaege: gruende.length,
        verwerfungsgruende: gruende,
    };
};
